import { AnalysisResult, ApiProvider } from '../types';
import { supabase } from './supabaseClient';

// Base64 conversion function - keeping as backup option
// (Gemini prefers URLs, but this can be useful for other APIs)
export const convertImageToBase64 = async (file: File): Promise<string> => {
  console.log('🔧 Converting image to Base64...');
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      try {
        const base64String = reader.result as string;
        console.log('✅ Base64 conversion successful, length:', base64String.length);
        resolve(base64String);
      } catch (error) {
        console.error('❌ Base64 conversion failed:', error);
        reject(new Error('Failed to convert image to Base64'));
      }
    };
    
    reader.onerror = () => {
      console.error('❌ FileReader error');
      reject(new Error('Failed to read image file'));
    };
    
    // Convert to Base64 data URL
    reader.readAsDataURL(file);
  });
};

export const uploadImageDirectly = async (file: File): Promise<string> => {
  console.log('� Starting fresh Supabase Storage upload...');
  
  // Get the current user for authentication
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  
  if (authError || !user) {
    console.error('❌ Authentication error:', authError);
    throw new Error('Please log in to upload images');
  }
  
  console.log('✅ User authenticated:', user.id);
  
  // Validate file type and size
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error(`Invalid file type: ${file.type}. Please use JPG, PNG, GIF, or WebP images.`);
  }
  
  const maxSize = 50 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    throw new Error(`File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum size is 10MB.`);
  }
  
  // Generate clean filename
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 8);
  let fileName = `${user.id}/${timestamp}_${randomId}.${fileExtension}`;
  
  console.log('� Upload filename:', fileName);
  
  try {
    // Attempt direct upload to the known bucket 'images'.
    // Note: listing buckets (`listBuckets()`) can be restricted for anon/public clients
    // depending on your Supabase RLS/policies. Trying the upload directly lets the
    // upload call fail with a clear error we can surface to the user.
    let { data: uploadData, error: uploadError } = await supabase.storage
      .from('images')
      .upload(fileName, file, {
        contentType: file.type,
        cacheControl: '3600',
        upsert: true
      });
    
    if (uploadError) {
      console.error('❌ Upload failed:', uploadError);

      // Common cause: bucket doesn't exist or client lacks permission to access it.
      const msg = typeof uploadError.message === 'string' ? uploadError.message.toLowerCase() : '';
      if (msg.includes('bucket') || msg.includes("doesn't exist") || msg.includes('not found')) {
        throw new Error("Upload failed: the 'images' bucket was not found or is not accessible with the current API key.\nPlease create a bucket named 'images' in your Supabase project (or adjust storage policies) and retry.");
      }

      // Handle duplicate filename retry
      if (uploadError.message && (uploadError.message.includes('duplicate') || uploadError.message.includes('already exists'))) {
        // Retry with a new filename
        const retryFileName = `${user.id}/${timestamp}_${Date.now()}_${randomId}.${fileExtension}`;
        console.log('🔄 Retrying with new filename:', retryFileName);
        
        const retryResult = await supabase.storage
          .from('images')
          .upload(retryFileName, file, {
            contentType: file.type,
            cacheControl: '3600',
            upsert: true
          });
        
        if (retryResult.error) {
          throw new Error(`Upload failed after retry: ${retryResult.error.message}`);
        }
        
        uploadData = retryResult.data;
        fileName = retryFileName;
      } else {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }
    }
    
    if (!uploadData) {
      throw new Error('Upload completed but no data returned');
    }
    
    console.log('✅ File uploaded successfully:', uploadData.path);
    
    // Get public URL
    const { data: urlData } = supabase.storage
      .from('images')
      .getPublicUrl(fileName);
    
    if (!urlData?.publicUrl) {
      throw new Error('Failed to generate public URL for uploaded image');
    }
    
    console.log('✅ Public URL generated:', urlData.publicUrl);
    
    // Verify URL accessibility (optional check)
    try {
      const testResponse = await fetch(urlData.publicUrl, { method: 'HEAD' });
      if (testResponse.ok) {
        console.log('✅ Upload URL verified and accessible');
      } else {
        console.warn('⚠️ Upload URL not immediately accessible, but should work shortly');
      }
    } catch (error) {
      console.warn('⚠️ Could not verify URL accessibility immediately:', error);
      // Don't fail the upload for this
    }
    
    return urlData.publicUrl;
    
  } catch (error) {
    console.error('❌ Upload process failed:', error);
    
    if (error instanceof Error) {
      // Re-throw known errors with clear messages
      if (error.message.includes('Image storage is not properly configured') ||
          error.message.includes('Storage service is currently unavailable') ||
          error.message.includes('Please log in to upload') ||
          error.message.includes('Invalid file type') ||
          error.message.includes('File too large')) {
        throw error;
      }
    }
    
    // Generic error for unknown issues
    throw new Error('Image upload failed. Please check your internet connection and try again.');
  }
};

export async function analyzeImageWithGeminiAPI(imageUrl: string, category?: string): Promise<AnalysisResult> {
  try {
    console.log('🤖 Starting Gemini analysis...');
    console.log('📊 Image URL:', imageUrl);
    console.log('📊 Category:', category || 'general');

    // Call Supabase Edge Function instead of local API
    const { data, error } = await supabase.functions.invoke('web-analyze-gemini', {
      body: { 
        imageUrl,
        category: category || 'general'
      }
    });

    console.log('📡 Gemini Edge Function response:', data, error);

    if (error) {
      console.error('❌ Gemini Edge Function error:', error);
      throw new Error(`Failed to analyze image with Gemini: ${error.message}`);
    }

    console.log('✅ Gemini API response received:', data);
    
    if (!data || !data.success) {
      console.error('❌ Gemini analysis failed:', data?.error);
      throw new Error(data?.error || 'Gemini analysis failed');
    }

    const transformedResult: AnalysisResult = {
      id: `gemini-${Date.now()}`,
      title: data.data.itemName || 'Unknown Product',
      description: data.data.itemDescription || 'No description available',
      value: data.data.estimatedValue || 'Price not available',
      aiExplanation: data.data.itemDescription || 'No AI explanation available',
      imageUrl: imageUrl,
      visualMatches: [],  // Initialize empty for now, will handle in future
      comparisonImages: data.data.comparisonImages || {},
      apiProvider: 'gemini' as ApiProvider,
      timestamp: Date.now(),
    };

    console.log('✅ Gemini analysis complete:', transformedResult);
    return transformedResult;

  } catch (error) {
    console.error('❌ Gemini analysis error:', error);
    throw error;
  }
}

export const analyzeImageWithSerpAPI = async (imageUrl: string): Promise<AnalysisResult> => {
  const response = await fetch('/api/analyze-image', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      imageUrl,
      provider: 'serpapi'
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to analyze image: ${response.status} ${errorText}`);
  }

  const data = await response.json();
  
  if (!data.success) {
    throw new Error(data.error || 'Analysis failed');
  }

  return {
    id: `serpapi-${Date.now()}`,
    title: data.data.title || 'Unknown Product',
    description: data.data.description || 'No description available',
    value: data.data.price || 'Price not available',
    aiExplanation: data.data.description || 'No explanation available',
    imageUrl: imageUrl,
    visualMatches: data.data.links || [],
    apiProvider: ApiProvider.SERPAPI,
    timestamp: Date.now(),
  };
};

export const analyzeImageWithSearchAPI = async (imageUrl: string): Promise<AnalysisResult> => {
  const response = await fetch('/api/analyze-image', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      imageUrl,
      provider: 'searchapi'
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to analyze image: ${response.status} ${errorText}`);
  }

  const data = await response.json();
  
  if (!data.success) {
    throw new Error(data.error || 'Analysis failed');
  }

  return data.data;
};

export const analyzeImageWithGrokAPI = async (imageData: string, category?: string): Promise<AnalysisResult> => {
  // Placeholder implementation for Grok API
  console.log('🤖 Grok API called with:', { imageData: imageData.length, category });
  
  return {
    id: `grok-${Date.now()}`,
    title: 'Grok Analysis - Not Implemented',
    description: 'Grok API integration not yet implemented',
    value: 'N/A',
    aiExplanation: 'This is a placeholder for future Grok API integration',
    imageUrl: imageData,
    apiProvider: 'grok' as ApiProvider,
    timestamp: Date.now(),
  };
};

export const analyzeImageWithOpenLensAPI = async (imageData: string, category?: string): Promise<AnalysisResult> => {
  // Placeholder implementation for OpenLens API
  console.log('🔍 OpenLens API called with:', { imageData: imageData.length, category });
  
  return {
    id: `openlens-${Date.now()}`,
    title: 'OpenLens Analysis - Not Implemented',
    description: 'OpenLens API integration not yet implemented',
    value: 'N/A',
    aiExplanation: 'This is a placeholder for future OpenLens API integration',
    imageUrl: imageData,
    apiProvider: 'openlens' as ApiProvider,
    timestamp: Date.now(),
  };
};
